import React, { useMemo, useState } from 'react';
import {
  SEAT_ORDER,
  SEAT_LABELS,
  TEAM_SEATS,
  seatOccupantLabel,
  teamOccupantLabel,
  visualSeatClass,
} from '../utils/tableSeats';
import { useHandMetrics, useOwnHandMetrics, isNarrowMobileLayout } from '../utils/handLayout';
import { useTrickCards } from '../hooks/useTrickCards';
import SpadesSeat from './SpadesSeat';
import SpadesCenter, { centerLayout } from './SpadesCenter';
import SpadesBidPanel from './SpadesBidPanel';
import TableHostPanel from './TableHostPanel';
import TableVacancyPrompt from './TableVacancyPrompt';
import SpadesHandResults from './SpadesHandResults';
import SpadesScoreboard from './SpadesScoreboard';
import HeartsScoreboard from './HeartsScoreboard';
import HeartsPassPanel from './HeartsPassPanel';
import HeartsHandResults from './HeartsHandResults';
import ScoreBagsDisplay from './ScoreBagsDisplay';
import TablePresenceButton from './TablePresenceButton';
import LastTrickPanel from './LastTrickPanel';
import GamePickPanel from './GamePickPanel';
import GinTable from './GinTable';

/** Server omits myHand/peekHand card faces until canViewHand allows — UI follows payload only. */
function handFacesFromServer(cards) {
  return !!(cards?.length && cards[0]?.id != null && cards[0]?.suit != null);
}

const GRID_SLOTS = {
  north: 'spades-seat--north',
  south: 'spades-seat--south',
  east: 'spades-seat--east',
  west: 'spades-seat--west',
};

function TableView({
  table,
  loggedInUserId,
  dealFlash,
  ginGameEvent = null,
  onBack,
  onSit,
  onStand,
  onHostAction,
  onPeekHand,
  onGameAction,
  tablePresence = { count: 0, users: [] },
  canModerate = false,
  onUserModClick,
  chatBubbles = [],
  ruleToast = null,
}) {
  const [watcherBotMenu, setWatcherBotMenu] = useState(null);
  const [showLastTrick, setShowLastTrick] = useState(false);
  const [gamePickId, setGamePickId] = useState(null);
  const handMetrics = useHandMetrics();
  const ownHandMetrics = useOwnHandMetrics();
  const mobileLayout = isNarrowMobileLayout();
  /* Center / trick cards match south (own) hand size on phone */
  const centerMetrics = mobileLayout ? ownHandMetrics : handMetrics;

  const mySeat = table?.viewerSeat;
  const perspectiveSeat = mySeat || table?.watcherPeekSeat;
  const gameTypeEarly = table?.gameType || table?.game?.gameType || 'spades';
  const isGinEarly = gameTypeEarly === 'gin';
  const trickLayout = useMemo(
    () => centerLayout(centerMetrics, { matchHandSize: mobileLayout }),
    [centerMetrics, mobileLayout],
  );
  // Gin has no tricks — skip trick pipeline so a gin game state cannot blank TableView
  const { trickCards, displayedTrickCounts, onFlyComplete, onGatherComplete } = useTrickCards(
    isGinEarly ? null : table?.game,
    perspectiveSeat,
    trickLayout,
  );

  if (!table) return null;

  const isWatching = table.isWatching;
  const isHost = table.hostKind === 'human'
    && table.hostUserId != null
    && loggedInUserId != null
    && Number(table.hostUserId) === Number(loggedInUserId);
  const watcherPeekSeat = isWatching ? table.watcherPeekSeat : null;
  const rotateForSeat = mySeat || watcherPeekSeat || null;
  const gamePlaying = table.status === 'playing';
  const hideWatcherHands = !!table.hideWatcherHands;
  const game = table.game;
  const gameOver = table.gameOver;
  const showHandResults = gamePlaying && !gameOver && game?.phase === 'hand_complete'
    && !!table.handResults;
  const needsGamePick = !gamePlaying && !gameOver && !!table.needsGamePick;
  const availableGames = table.availableGames || [];
  // Server sets needsGamePick for multi-game lobbies until host confirms
  const showGamePick = isHost && needsGamePick;
  const showHostPanel = isHost && !gamePlaying && !gameOver && !needsGamePick;
  const showVacancyPrompt = isHost && gamePlaying && !gameOver
    && !!table.siteBotsEnabled
    && (table.hostVacancyPrompt?.seats?.length > 0);
  const lastTrick = game?.lastTrick || null;
  const gameType = gameTypeEarly;
  const isHearts = gameType === 'hearts';
  const isGin = isGinEarly;
  const gameLabel = needsGamePick
    ? 'Choose game'
    : (table.availableGames || []).find((g) => g.id === gameType)?.label
      || (isHearts ? 'Hearts' : isGin ? 'Gin' : 'Spades');
  const hostSettings = isHearts
    ? (table.heartsSettings || {})
    : isGin
      ? (table.ginSettings || {})
      : (table.spadesSettings || {});
  const doubleNilEnabled = !!table.spadesSettings?.doubleNilEnabled;
  const handRevealed = handFacesFromServer(table.myHand);
  const peekHandRevealed = handFacesFromServer(table.peekHand);
  const showPlayerHand = mySeat && gamePlaying && !gameOver;
  const showWatcherHand = isWatching && gamePlaying && watcherPeekSeat && !hideWatcherHands && !gameOver;
  const myHandCount = table.myHand?.length || table.handCounts?.[mySeat] || 0;
  const peekHandCount = table.peekHand?.length || table.handCounts?.[watcherPeekSeat] || 0;
  const isPassing = isHearts && game?.phase === 'passing';
  const passSelectedCount = isPassing
    ? (table.myHand || []).filter((c) => c?.selected || (c?.raised && !c?.received)).length
    : 0;
  const receivedCards = isHearts
    ? (table.myHand || []).filter((c) => c?.received)
    : [];
  const passRevealPending = isHearts
    && !!mySeat
    && !!(game?.passReveal?.[mySeat]?.pending || receivedCards.length > 0);

  const countSeatedHumans = () => SEAT_ORDER.filter(
    (s) => table.seats?.[s]?.type === 'player',
  ).length;

  const isLastHumanInPlay = () => gamePlaying && !gameOver && countSeatedHumans() <= 1 && !!mySeat;

  const confirmLastHumanLeave = (actionLabel = 'leave') => {
    if (!isLastHumanInPlay()) return true;
    return window.confirm(
      `You are the last player at this table. If you ${actionLabel}, the game will end and the table will reset. Are you sure?`,
    );
  };

  const handleStand = () => {
    if (!confirmLastHumanLeave('stand up')) return;
    onStand();
  };

  /** Score chip for seat: singles = each occupant; teams = visual north + east only. */
  const seatScoreFor = (absoluteSeat, visualClass) => {
    const sb = table.scoreboard;
    if (!sb || !gamePlaying || gameOver) return null;
    if (isHearts || sb.scoringMode === 'singles' || sb.gameType === 'hearts') {
      if (!table.seats?.[absoluteSeat]) return null;
      const p = sb.players?.[absoluteSeat];
      return {
        score: p?.score ?? 0,
        bags: isHearts ? null : (p?.bags ?? 0),
        hideBags: isHearts,
      };
    }
    const mode = sb.scoringMode || table.spadesSettings?.scoringMode || 'teams';
    if (mode === 'singles') {
      if (!table.seats?.[absoluteSeat]) return null;
      const p = sb.players?.[absoluteSeat];
      return { score: p?.score ?? 0, bags: p?.bags ?? 0 };
    }
    // Teams: one chip per partnership, anchored on visual north & east
    if (visualClass !== 'north' && visualClass !== 'east') return null;
    const teamKey = (absoluteSeat === 'north' || absoluteSeat === 'south') ? 'ns' : 'ew';
    const team = sb.teams?.[teamKey];
    return { score: team?.score ?? 0, bags: team?.bags ?? 0 };
  };

  const handleSeatClick = (absoluteSeat) => {
    const occ = table.seats?.[absoluteSeat];
    if (mySeat) {
      if (absoluteSeat === mySeat
        || (occ?.type === 'player' && Number(occ.userId) === Number(loggedInUserId))) {
        handleStand();
      }
      return;
    }
    if (!occ || occ.type === 'site_bot' || occ.type === 'external_bot') {
      onSit(absoluteSeat);
    }
  };

  const handleStartGame = () => {
    onHostAction('start-game');
  };

  const handleDismissResults = () => {
    if (window.opener) {
      window.close();
      return;
    }
    onHostAction('dismiss-results');
  };

  const emitGame = (action, payload) => onGameAction?.(action, payload);

  const nsTeamLabel = teamOccupantLabel(table.seats, TEAM_SEATS.ns);
  const ewTeamLabel = teamOccupantLabel(table.seats, TEAM_SEATS.ew);

  let winnerLabel = null;
  if (gameOver) {
    if (gameOver.scoringMode === 'singles') {
      const winSeat = gameOver.winner;
      winnerLabel = winSeat
        ? seatOccupantLabel(winSeat, table.seats?.[winSeat])
        : gameOver.winnerLabel;
    } else if (gameOver.winner === 'ns') {
      winnerLabel = nsTeamLabel;
    } else if (gameOver.winner === 'ew') {
      winnerLabel = ewTeamLabel;
    } else {
      winnerLabel = gameOver.winnerLabel;
    }
  }

  let tableHintText = '';
  let tableHintClass = 'table-view-hint';
  if (!mySeat) {
    tableHintText = isWatching
      ? (gamePlaying && !hideWatcherHands
        ? 'Double-click a player to watch their hand. Click a bot to watch or take over that seat.'
        : gamePlaying
          ? 'Click a bot seat to take over for that bot.'
          : 'Click an open seat (or bot seat) to sit down.')
      : 'Select a seat to join the table.';
  } else if (showWatcherHand) {
    tableHintClass = 'table-view-hint table-view-hint--peek';
    tableHintText = `Viewing ${SEAT_LABELS[watcherPeekSeat]}'s hand — double-click again to stop.`;
  } else if (showPlayerHand && handRevealed) {
    if (isPassing) {
      tableHintText = `Click cards to select 3 to pass (${passSelectedCount}/3).`;
    } else if (passRevealPending) {
      tableHintText = 'New cards are held up — press OK or play when ready.';
    } else {
      tableHintText = game?.phase === 'playing'
        ? `Click a card to raise it; double-click to play. ${game.turnSeat === mySeat ? 'Your turn.' : `Waiting for ${SEAT_LABELS[game.turnSeat] || '…'}.`}`
        : 'Click cards in your hand on the table to raise them.';
    }
  }

  // ----- Gin Rummy (2-player layout) -----
  // Only after gin is confirmed. During multi-game pick (needsGamePick) the last
  // gameType may still be "gin" — fall through to the 4-seat felt so players can
  // sit / become host. Hiding seats here locked empty tables after a gin reset.
  if (isGin && !needsGamePick) {
    return (
      <div className="table-view table-view--gin-host">
        <div className="table-view-toolbar">
          <button type="button" className="table-view-back" onClick={onBack}>
            ← Back to tables
          </button>
          <div className="table-view-title-wrap">
            <h2>{table.title}</h2>
            <span className="table-view-game">Gin</span>
            <span className={`table-view-status table-view-status--${table.status}`}>
              {gameOver ? 'Finished' : table.status === 'playing' ? 'In progress' : 'Open'}
            </span>
          </div>
          {mySeat && (
            <button type="button" className="table-action-btn table-action-btn--stand" onClick={handleStand}>
              Stand up
            </button>
          )}
        </div>
        <GinTable
          table={{ ...table, isHost }}
          loggedInUserId={loggedInUserId}
          ginGameEvent={ginGameEvent}
          onBack={onBack}
          onStand={onStand}
          onSit={onSit}
          onHostAction={onHostAction}
          onGameAction={onGameAction}
        />
      </div>
    );
  }

  return (
    <div className={`table-view table-view--spades table-view--${gameType}`}>
      <div className="table-view-toolbar">
        <button type="button" className="table-view-back" onClick={onBack}>
          ← Back to tables
        </button>
        <div className="table-view-title-wrap">
          <h2>{table.title}</h2>
          <span className="table-view-game">{gameLabel}</span>
          <span className={`table-view-status table-view-status--${table.status}`}>
            {gameOver ? 'Finished' : table.status === 'playing' ? 'In progress' : 'Open'}
          </span>
        </div>
        {mySeat && (
          <button type="button" className="table-action-btn table-action-btn--stand" onClick={handleStand}>
            Stand up
          </button>
        )}
      </div>

      <div className="table-view-hint-slot" aria-live="polite">
        <p className={tableHintClass}>{tableHintText || '\u00A0'}</p>
      </div>

      <div className="spades-table-wrap">
        {/* Floating chat bubbles (mobile / compact table mode) */}
        {chatBubbles.length > 0 && (
          <div className="table-chat-bubbles" aria-live="polite">
            {chatBubbles.map((b) => (
              <div
                key={b.id}
                className={[
                  'table-chat-bubble',
                  b.type === 'system' ? 'table-chat-bubble--system' : '',
                  b.highlight ? 'table-chat-bubble--highlight' : '',
                ].filter(Boolean).join(' ')}
              >
                {b.type === 'system' ? (
                  b.text
                ) : (
                  <>
                    <strong>{b.username || '?'}</strong>
                    <span>: {b.text}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="spades-felt">
          {/* Illegal play / pass / bid — single toast above south hand (timer resets on new msg) */}
          {ruleToast && (
            <div className="table-rule-toast-slot" aria-live="assertive" role="status">
              <div className="table-chat-bubble table-rule-toast" key={ruleToast}>
                {ruleToast}
              </div>
            </div>
          )}
          <div className="spades-felt-chrome">
            <TablePresenceButton
              users={tablePresence.users || []}
              count={tablePresence.count || 0}
              currentUserId={loggedInUserId}
              canModerate={canModerate}
              onUserClick={onUserModClick}
            />
          </div>
          {/* Top-left: score dock + optional last-trick history */}
          <div className="spades-top-left-tools">
            <div className="spades-score-dock">
              {isHearts ? (
                <HeartsScoreboard
                  scoreboard={table.scoreboard}
                  settings={table.heartsSettings}
                  seats={table.seats}
                  compact
                  variant="dock"
                />
              ) : (
                <SpadesScoreboard
                  scoreboard={table.scoreboard}
                  settings={table.spadesSettings}
                  seats={table.seats}
                  compact
                  variant="dock"
                />
              )}
            </div>
            {gamePlaying && (
              <LastTrickPanel
                lastTrick={lastTrick}
                open={showLastTrick}
                onToggle={() => setShowLastTrick((v) => !v)}
                viewerSeat={mySeat || rotateForSeat || null}
              />
            )}
          </div>
          {SEAT_ORDER.map((absoluteSeat) => {
            const visualClass = visualSeatClass(absoluteSeat, rotateForSeat);
            const occ = table.seats?.[absoluteSeat];
            const isViewerSeat = mySeat != null && absoluteSeat === mySeat;
            const isYou = isViewerSeat
              || (occ?.type === 'player' && Number(occ.userId) === Number(loggedInUserId));
            const isBot = occ?.type === 'site_bot' || occ?.type === 'external_bot';
            const empty = !occ;
            const canPick = !mySeat && (empty || isBot);

            return (
              <SpadesSeat
                key={absoluteSeat}
                absoluteSeat={absoluteSeat}
                visualClass={visualClass}
                gridClass={GRID_SLOTS[visualClass]}
                occupant={occ}
                isYou={isYou}
                canPick={canPick}
                gamePlaying={gamePlaying && !gameOver}
                seatScore={seatScoreFor(absoluteSeat, visualClass)}
                game={game}
                isWatching={isWatching}
                hideWatcherHands={hideWatcherHands}
                handCounts={table.handCounts}
                trickStackCount={displayedTrickCounts?.[absoluteSeat] ?? 0}
                trickStackMode={isHearts ? 'points' : 'tricks'}
                trickStackPoints={
                  isHearts
                    ? (game?.handPoints?.[absoluteSeat]
                      ?? table.scoreboard?.players?.[absoluteSeat]?.handPoints
                      ?? 0)
                    : 0
                }
                watcherPeekSeat={table.watcherPeekSeat}
                onSeatClick={handleSeatClick}
                onPeekHand={onPeekHand}
                onWatcherBotClick={(seat, label) => {
                  setWatcherBotMenu({ seat, label: label || SEAT_LABELS[seat] });
                }}
                viewerSeat={mySeat}
                handMetrics={isViewerSeat ? ownHandMetrics : handMetrics}
                playerHand={isViewerSeat && mySeat ? {
                  cards: table.myHand,
                  faceUp: handRevealed,
                  count: myHandCount || table.handCounts?.[mySeat] || 0,
                  canRaise: showPlayerHand && handRevealed
                    && (isPassing || game?.phase === 'playing'
                      || game?.phase === 'bidding'
                      || game?.phase === 'bidding_choice'),
                  canPlay: handRevealed && game?.phase === 'playing' && !isPassing,
                  onRaiseCard: (cardId) => emitGame(
                    isPassing ? 'toggle-pass-card' : 'raise-card',
                    { cardId },
                  ),
                  onPlayCard: (cardId) => emitGame('play-card', { cardId }),
                } : null}
                peekHand={
                  absoluteSeat === watcherPeekSeat && showWatcherHand
                    ? {
                      cards: table.peekHand,
                      faceUp: peekHandRevealed,
                      count: peekHandCount,
                    }
                    : null
                }
              />
            );
          })}
          <SpadesCenter
            title={table.title}
            settings={hostSettings}
            isHost={isHost}
            game={game}
            gamePlaying={gamePlaying && !gameOver}
            handMetrics={centerMetrics}
            matchHandSize={mobileLayout}
            dealFlash={dealFlash}
            trickCards={trickCards}
            onFlyComplete={onFlyComplete}
            onGatherComplete={onGatherComplete}
          />
          <div
            className={[
              'spades-table-overlay',
              showVacancyPrompt ? 'spades-table-overlay--host' : '',
            ].filter(Boolean).join(' ')}
          >
            <TableVacancyPrompt
              prompt={showVacancyPrompt ? table.hostVacancyPrompt : null}
              floating
              onFillBots={() => onHostAction('fill-vacant-seats')}
              onWaitForPlayer={() => onHostAction('wait-vacant-seats')}
            />
          </div>
          {watcherBotMenu && isWatching && gamePlaying && !gameOver && (
            <div
              className="spades-watcher-bot-menu-backdrop"
              role="presentation"
              onClick={() => setWatcherBotMenu(null)}
            >
              <div
                className="spades-watcher-bot-menu"
                role="dialog"
                aria-modal="true"
                aria-labelledby="spades-watcher-bot-menu-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h4 id="spades-watcher-bot-menu-title">
                  {watcherBotMenu.label}
                  {' '}
                  <span className="spades-watcher-bot-menu-seat">
                    ({SEAT_LABELS[watcherBotMenu.seat]})
                  </span>
                </h4>
                {!hideWatcherHands && (
                  <button
                    type="button"
                    className="table-action-btn"
                    onClick={() => {
                      onPeekHand(watcherBotMenu.seat);
                      setWatcherBotMenu(null);
                    }}
                  >
                    {table.watcherPeekSeat === watcherBotMenu.seat
                      ? 'Stop watching hand'
                      : 'Watch / switch to this hand'}
                  </button>
                )}
                <button
                  type="button"
                  className="table-action-btn table-action-btn--host"
                  onClick={() => {
                    onSit(watcherBotMenu.seat);
                    setWatcherBotMenu(null);
                  }}
                >
                  Take over this seat
                </button>
                <button
                  type="button"
                  className="table-action-btn table-action-btn--small"
                  onClick={() => setWatcherBotMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {gameOver && (
            <div className="spades-game-over" role="dialog" aria-modal="true" aria-labelledby="spades-game-over-title">
              <div className="spades-game-over-panel">
                <h3 id="spades-game-over-title">Game over</h3>
                <p className="spades-game-over-winner">
                  {winnerLabel} wins!
                </p>
                <div className="spades-game-over-scores">
                  {(gameOver.scoringMode === 'singles' || gameOver.gameType === 'hearts') ? (
                    ['north', 'south', 'east', 'west'].map((seat) => {
                      const ps = gameOver.players?.[seat];
                      const occ = table.seats?.[seat];
                      if (!ps && !occ) return null;
                      return (
                        <div key={seat} className="spades-game-over-team">
                          <span>{seatOccupantLabel(seat, occ)}</span>
                          {gameOver.gameType === 'hearts' || isHearts ? (
                            <span className="spades-score-value">{ps?.score ?? 0}</span>
                          ) : (
                            <ScoreBagsDisplay score={ps?.score ?? 0} bags={ps?.bags ?? 0} />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div className="spades-game-over-team">
                        <span>{nsTeamLabel}</span>
                        <ScoreBagsDisplay
                          score={gameOver.teams?.ns?.score ?? 0}
                          bags={gameOver.teams?.ns?.bags ?? 0}
                        />
                      </div>
                      <div className="spades-game-over-team">
                        <span>{ewTeamLabel}</span>
                        <ScoreBagsDisplay
                          score={gameOver.teams?.ew?.score ?? 0}
                          bags={gameOver.teams?.ew?.bags ?? 0}
                        />
                      </div>
                    </>
                  )}
                </div>
                <p className="spades-game-over-hand">After hand {gameOver.hand ?? '—'}</p>
                <div className="spades-game-over-actions">
                  <button
                    type="button"
                    className="table-action-btn"
                    onClick={handleDismissResults}
                  >
                    Close window
                  </button>
                  {isHost && (
                    <button
                      type="button"
                      className="table-action-btn table-action-btn--host"
                      onClick={() => onHostAction('new-game')}
                    >
                      New game
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Host / hand results / bid / game pick — full table float */}
        <div className="spades-float-layer" aria-live="polite">
          {showGamePick && (
            <GamePickPanel
              floating
              games={availableGames}
              selectedId={gamePickId || table.gameType || availableGames.find((g) => g.playable)?.id}
              onSelect={setGamePickId}
              onConfirm={(gameId) => onHostAction('set-game', { gameType: gameId })}
            />
          )}
          {!showGamePick && needsGamePick && !isHost && (
            <div className="spades-bid-panel spades-bid-panel--float" role="status">
              <p className="spades-bid-prompt">Waiting for host to choose a game…</p>
            </div>
          )}
          <TableHostPanel
            visible={showHostPanel}
            floating
            gameType={gameType}
            settings={hostSettings}
            lockGameSettings={!!table.lockGameSettings}
            hostSettingsLocked={!!table.hostSettingsLocked}
            watchersEnabled={!!table.watchersEnabled}
            siteBotsEnabled={!!table.siteBotsEnabled}
            onStartGame={handleStartGame}
            onHostAction={onHostAction}
          />
          {isHearts ? (
            <HeartsHandResults
              handResults={showHandResults ? table.handResults : null}
              seats={table.seats}
              isHost={isHost}
              floating
              onNextHand={() => onHostAction('next-hand')}
            />
          ) : (
            <SpadesHandResults
              handResults={showHandResults ? table.handResults : null}
              seats={table.seats}
              isHost={isHost}
              floating
              onNextHand={() => onHostAction('next-hand')}
            />
          )}
          {isHearts && (
            <HeartsPassPanel
              key={`pass-${game?.handNumber ?? 0}-${game?.phase ?? 'idle'}-${passRevealPending ? 'recv' : 'sel'}`}
              floating
              phase={game?.phase}
              passDirection={game?.passDirection}
              passLabel={game?.passLabel}
              viewerSeat={mySeat}
              passStatus={game?.passStatus}
              selectedCount={passSelectedCount}
              onConfirmPass={() => emitGame('confirm-pass', {})}
              receivedCards={receivedCards}
              passRevealPending={passRevealPending}
              onAckReceivedPass={() => emitGame('ack-received-pass', {})}
              receivedFromSeat={mySeat ? game?.passReveal?.[mySeat]?.fromSeat : null}
              seats={table.seats}
            />
          )}
          {!isHearts && (
            <SpadesBidPanel
              key={`bid-${game?.handNumber ?? 0}-${game?.phase ?? 'idle'}`}
              floating
              phase={game?.phase}
              turnSeat={game?.turnSeat}
              viewerSeat={mySeat}
              doubleNilEnabled={doubleNilEnabled}
              viewerBid={game?.bids?.[mySeat]}
              viewerBiddingChoice={game?.biddingChoice?.[mySeat]}
              onBiddingChoice={(choice) => emitGame('bidding-choice', { choice })}
              onBid={(bid) => emitGame('bid', { bid })}
            />
          )}
        </div>
      </div>

      {mySeat && (
        <div className="table-view-actions">
          <button type="button" className="table-action-btn" onClick={handleStand}>
            Stand up
          </button>
        </div>
      )}
    </div>
  );
}

export default TableView;